import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useEffect, useState, useRef } from "react";
import classNames from "classnames";
import { format } from "date-fns";

interface WorkoutSelectProps {
  id: string;
  className?: string;
  label?: string;
  initialSelectedWorkout: string;
  workouts: Workout[];
  onSelect: (workout: Workout) => void;
}

const WorkoutSelect: React.FC<WorkoutSelectProps> = ({
  id,
  className,
  label,
  initialSelectedWorkout,
  workouts,
  onSelect,
}) => {
  const [selectedWorkout, setSelectedWorkout] = useState<Workout>();
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!selectedWorkout && initialSelectedWorkout) {
      const initialWorkout = workouts.find(
        (workout) => workout.id === initialSelectedWorkout
      );
      setSelectedWorkout(initialWorkout);
    }
  }, [initialSelectedWorkout, selectedWorkout, workouts]);

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (
        selectRef.current &&
        selectRef.current.contains(event.target) === false
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectRef]);

  const handleSelectClicked = () => {
    if (isOpen) {
      setIsOpen(false);
      selectRef.current?.blur();
    } else {
      setIsOpen(true);
      selectRef.current?.focus();
    }
  };

  const handleValueChanged = (workout: Workout) => {
    setSelectedWorkout(workout);
    setIsOpen(false);
    onSelect(workout);
  };

  if (!selectedWorkout) return;

  return (
    <div className={`relative ${className}`} ref={selectRef}>
      {label && (
        <label
          htmlFor={id}
          className="block mb-1 text-sm font-medium text-gray-700"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <div className="w-[1px] h-[40px] bg-gray-400 absolute right-[40px] top-[28px] -translate-y-1/2 pointer-events-none" />
        <ChevronDownIcon
          className="h-5 w-auto text-gray-400 absolute right-3 top-[28px] -translate-y-1/2 pointer-events-none"
          aria-hidden="true"
        />
        <div
          id={id}
          className={classNames(
            "block w-full pl-3 pr-[52px] py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm",
            {
              "outline-none ring-1 ring-indigo-500 border-indigo-500": isOpen,
            }
          )}
          tabIndex={0}
          onClick={handleSelectClicked}
        >
          <div className="flex flex-col">
            <span className="font-normal">
              {selectedWorkout.title
                ? selectedWorkout.title
                : "Untitled Workout"}
            </span>
            <span className="text-sm text-gray-500 font-light">
              {selectedWorkout &&
                format(selectedWorkout.date, "EEE, MMMM dd, yyyy")}
            </span>
          </div>
        </div>
      </div>
      {isOpen && (
        <div className="absolute z-50 w-full left-[-1px] top-[calc(100%+8px)]">
          <div className="w-[calc(100%+2px)] flex flex-col bg-white rounded-md overflow-y-scroll max-h-[234px] py-1 shadow-lg ring-1 ring-black ring-opacity-5">
            {workouts.map((workout) => {
              return (
                <div
                  className={classNames(
                    `relative text-left px-3 py-[9px] text-sm w-full hover:cursor-pointer flex flex-row`,
                    {
                      "bg-indigo-200": selectedWorkout.id === workout.id,
                    }
                  )}
                  key={workout.id}
                  onClick={() => handleValueChanged(workout)}
                >
                  <div className="flex flex-col">
                    <span className="font-normal text-gray-700">
                      {workout.title ? workout.title : "Untitled Workout"}
                    </span>
                    <span className="text-sm text-gray-500 font-light">
                      {format(workout.date, "EEE, MMMM dd, yyyy")}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkoutSelect;
